import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Boxes, Plus, Trash2, ArrowDownCircle, ArrowUpCircle } from "lucide-react";
import { rank } from "../../lib/rbac";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CATEGORIES = ["Stationary", "Housekeeping", "Misc"];

export default function StockRegister() {
  const [data, setData] = useState({ entries: [], balances: [] });
  const [user, setUser] = useState(null);
  const [form, setForm] = useState({ category: "Stationary", item: "", direction: "IN", qty: 1, note: "", date: new Date().toISOString().split("T")[0] });

  const fetchAll = useCallback(async () => {
    try {
      const [r, me] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/stock-ledger`),
        axios.get(`${BACKEND_URL}/api/auth/me`),
      ]);
      setData(r.data); setUser(me.data.user);
    } catch (e) { console.error(e); }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const canDelete = user && rank(user.role) >= 60;

  const addEntry = async (e) => {
    e.preventDefault();
    try { await axios.post(`${BACKEND_URL}/api/stock-ledger`, { ...form, qty: parseFloat(form.qty) }); setForm({ ...form, item: "", qty: 1, note: "" }); fetchAll(); }
    catch (err) { alert(err.response?.data?.detail || "Not allowed"); }
  };
  const delEntry = async (id) => { try { await axios.delete(`${BACKEND_URL}/api/stock-ledger/${id}`); fetchAll(); } catch (e) { alert(e.response?.data?.detail || "Not allowed"); } };

  const inp = "w-full bg-card_bg border border-border_soft rounded-xl p-3 text-text_primary text-sm";

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-8 duration-700" data-testid="stock-register-page">
      <div>
        <h1 className="text-4xl font-serif text-text_primary mb-3">Stock Register</h1>
        <p className="text-text_secondary text-sm">Manually track stationary, housekeeping and miscellaneous stock for your branch.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <form onSubmit={addEntry} className="bg-card_bg border border-border_soft rounded-2xl p-6 space-y-4 lg:col-span-1 h-fit">
          <h2 className="text-lg font-bold text-text_primary flex items-center gap-2"><Plus size={18} className="text-gold" /> New Entry</h2>
          <select data-testid="stock-category" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className={inp}>{CATEGORIES.map(c => <option key={c}>{c}</option>)}</select>
          <input required data-testid="stock-item" placeholder="Item name" value={form.item} onChange={e => setForm({ ...form, item: e.target.value })} className={inp} />
          <div className="flex gap-2">
            <button type="button" onClick={() => setForm({ ...form, direction: "IN" })} className={`flex-1 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-1 ${form.direction === "IN" ? "bg-accent_success/20 border border-accent_success text-accent_success" : "bg-main_bg border border-border_soft text-text_secondary"}`}><ArrowDownCircle size={15} /> Stock In</button>
            <button type="button" onClick={() => setForm({ ...form, direction: "OUT" })} className={`flex-1 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-1 ${form.direction === "OUT" ? "bg-primary/20 border border-primary text-gold" : "bg-main_bg border border-border_soft text-text_secondary"}`}><ArrowUpCircle size={15} /> Stock Out</button>
          </div>
          <input required type="number" min="0" step="any" placeholder="Quantity" value={form.qty} onChange={e => setForm({ ...form, qty: e.target.value })} className={inp} data-testid="stock-qty" />
          <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className={inp} />
          <input placeholder="Note (optional)" value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} className={inp} />
          <button type="submit" data-testid="add-stock-btn" className="w-full bg-primary text-[#111827] font-bold py-3 rounded-xl hover:bg-primary_hover transition-colors">Save Entry</button>
        </form>

        <div className="lg:col-span-2 space-y-6">
          <div className="bg-card_bg border border-border_soft rounded-2xl p-6">
            <h3 className="text-sm font-bold text-gold uppercase tracking-widest mb-4 flex items-center gap-2"><Boxes size={16} /> Current Balances</h3>
            {data.balances.length === 0 && <p className="text-text_secondary text-sm">No stock recorded yet.</p>}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {data.balances.map((b, i) => (
                <div key={i} className="bg-main_bg border border-border_soft/60 rounded-xl p-3">
                  <p className="text-[10px] uppercase tracking-wider text-text_secondary">{b.category}</p>
                  <p className="text-sm text-text_primary font-semibold truncate">{b.item}</p>
                  <p className={`text-xl font-bold ${b.balance < 0 ? "text-accent_danger" : "text-gold"}`}>{b.balance}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-card_bg border border-border_soft rounded-2xl overflow-hidden">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-border_soft/50"><tr>
                <th className="px-5 py-3 text-[10px] uppercase tracking-widest text-text_secondary font-bold">Date</th>
                <th className="px-5 py-3 text-[10px] uppercase tracking-widest text-text_secondary font-bold">Category</th>
                <th className="px-5 py-3 text-[10px] uppercase tracking-widest text-text_secondary font-bold">Item</th>
                <th className="px-5 py-3 text-[10px] uppercase tracking-widest text-text_secondary font-bold">In/Out</th>
                <th className="px-5 py-3 text-[10px] uppercase tracking-widest text-text_secondary font-bold text-right">Qty</th>
                <th className="px-5 py-3"></th>
              </tr></thead>
              <tbody className="divide-y divide-border_soft/40">
                {data.entries.length === 0 && <tr><td colSpan="6" className="px-5 py-8 text-center text-text_secondary">No entries yet.</td></tr>}
                {data.entries.map((e) => (
                  <tr key={e.id}>
                    <td className="px-5 py-3 text-text_secondary font-mono">{e.date}</td>
                    <td className="px-5 py-3 text-text_secondary">{e.category}</td>
                    <td className="px-5 py-3 text-text_primary font-semibold">{e.item}{e.note ? <span className="text-text_secondary font-normal text-xs"> · {e.note}</span> : ""}</td>
                    <td className="px-5 py-3"><span className={e.direction === "IN" ? "text-accent_success font-bold" : "text-gold font-bold"}>{e.direction}</span></td>
                    <td className="px-5 py-3 text-right text-text_primary font-bold">{e.qty}</td>
                    <td className="px-5 py-3 text-right">{canDelete && <button onClick={() => delEntry(e.id)} className="text-text_secondary hover:text-accent_danger"><Trash2 size={15} /></button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
